import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskLinesInView, Reveal } from "../components/Reveal";
import { BrandMarquee } from "../components/BrandMarquee";
import { MANIFESTO, IMAGES, CHECKOUT_URL } from "../lib/brand";

const Chapter = ({ chapter, index }) => {
  const flip = index % 2 === 1;
  const media = [
    { src: IMAGES.origin, className: "aspect-[4/3] w-full object-cover" },
    { src: IMAGES.craft, className: "aspect-[4/3] w-full object-cover" },
    { src: IMAGES.parkJam, className: "aspect-[4/3] w-full object-cover" },
    { src: IMAGES.jamGroup, className: "aspect-[4/3] w-full object-cover" },
  ];
  return (
    <div
      className={`grid items-center gap-10 lg:grid-cols-2 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}
      data-testid={`manifesto-chapter-${index + 1}`}
    >
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl">
          <img src={media[index].src} alt={chapter.title} className={media[index].className} />
          <span className="absolute left-5 top-4 font-display text-7xl text-white mix-blend-difference">
            {chapter.n}
          </span>
        </div>
      </Reveal>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#184786]">Chapter {chapter.n}</p>
        <h2 className="font-display mt-3 text-4xl uppercase leading-[0.95] text-[#212121] sm:text-5xl">
          {chapter.title}
        </h2>
        <div className="mt-5 max-w-lg space-y-4 text-base leading-relaxed text-[#212121]/70 sm:text-lg">
          {chapter.text.split("\n\n").map((para, i) => (
            <p key={i}>
              {para.split("\n").map((line, k) => (
                <span key={k}>
                  {k > 0 && <br />}
                  {line.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
                    seg.startsWith("**") && seg.endsWith("**") ? (
                      <strong key={j} className="font-semibold text-[#212121]">{seg.slice(2, -2)}</strong>
                    ) : (
                      seg
                    )
                  )}
                </span>
              ))}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section ref={ref} className="grain relative overflow-hidden bg-[#184786] px-5 pb-16 pt-32 sm:px-8">
        <motion.div style={{ y }} className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-[#EE4026]/40 blur-3xl" />
        <div className="relative mx-auto max-w-[1400px]">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60">About Featherbomb</p>
          <h1 className="font-display mt-5 text-white">
            <MaskLinesInView
              className="block text-[13vw] uppercase leading-[0.95] sm:text-8xl sm:leading-[0.85]"
              lines={["A little toy", <span key="a" className="text-[#EE4026]">with a big</span>, "obsession."]}
            />
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            We believe the best play is the kind you don't have to schedule. No court to book, no team to
            organize... just a featherbomb, your feet, and the people around you. This is the story of how we
            made it.
          </p>
        </div>
      </section>

      <BrandMarquee />

      {/* Chapters */}
      <section className="bg-[#F5F5F5] px-5 py-24 sm:px-8 sm:py-32">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-28">
          {MANIFESTO.map((c, i) => (
            <Chapter key={c.n} chapter={c} index={i} />
          ))}
        </div>
      </section>

      {/* Values band */}
      <section className="bg-[#212121] px-5 py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="font-display max-w-3xl text-3xl uppercase leading-[1] text-white sm:text-5xl">
              Built by players, for players — <span className="text-[#EE4026]">tested in driveways, parks, and rooftops worldwide.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {[
              ["Obsessive craft", "Every Featherbomb is weight-tuned and flight-tested before it ships."],
              ["Radically simple", "No net, no rules, no setup. Open the box and play."],
              ["Endlessly social", "One Featherbomb starts a circle. A circle starts a movement."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                  <p className="font-display text-xl uppercase text-white">{t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 flex justify-center">
            <a href={CHECKOUT_URL} target="_blank" rel="noreferrer" className="btn-primary flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wide">
              Grab your Featherbomb <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
